import { createContext, useContext, useEffect, useState } from "react";
import {
  requestNotificationPermissionWeb,
  onMessageListener,
  getExistingFCMToken,
} from "@/config/firebase";
import { useUser } from "@/hooks/useUser";
import { useUpdateUser } from "@/hooks/auth-hooks";
import { useRouter } from "@tanstack/react-router";
import { useNotification } from "@/hooks/useNotification";
import { useMobile } from "@/providers/mobileProvider";

interface PushNotificationContextType {
  isNotificationSupported: boolean;
  isNotificationPermissionGranted: boolean;
  requestPermission: () => Promise<boolean>;
}

const PushNotificationContext = createContext<PushNotificationContextType>({
  isNotificationSupported: false,
  isNotificationPermissionGranted: false,
  requestPermission: async () => false,
});

export const usePushNotification = () => useContext(PushNotificationContext);

export function PushNotificationProvider({ children }: { children: React.ReactNode }) {
  const [isNotificationSupported, setIsNotificationSupported] = useState(false);
  const [isNotificationPermissionGranted, setIsNotificationPermissionGranted] = useState(false);
  const { userId } = useUser();
  const router = useRouter();
  const { mutateAsync } = useUpdateUser();
  const { showNotification } = useNotification();
  const { isIOS, isAndroid } = useMobile();

  // Check if the browser supports Firebase messaging
  const isFirebaseMessagingSupported = () => {
    if (typeof window === "undefined") return false;

    // Check for required APIs
    const hasServiceWorker = "serviceWorker" in navigator;
    const hasNotification = "Notification" in window;
    const hasPushManager = "PushManager" in window;

    // Check if we're in a secure context (required for service workers)
    const isSecureContext = window.isSecureContext || location.protocol === "https:";

    return hasServiceWorker && hasNotification && hasPushManager && isSecureContext;
  };

  // Check if we have the native bridge for iOS
  const hasNativeBridge =
    typeof window !== "undefined" && !!window.webkit?.messageHandlers?.["push-permission-request"];

  // Handle iOS permission state changes
  useEffect(() => {
    if (!isIOS || !hasNativeBridge) return;

    // Listen for permission state changes from iOS
    const handlePermissionState = (event: CustomEvent) => {
      const state = event.detail;
      const isGranted = state === "authorized" || state === "provisional" || state === "ephemeral";
      setIsNotificationPermissionGranted(isGranted);
      setIsNotificationSupported(true);

      // Request FCM token if permission is already granted
      if (isGranted) {
        window.webkit?.messageHandlers["push-token"].postMessage({});
      }
    };

    // Check initial permission state
    window.webkit?.messageHandlers["push-permission-state"].postMessage({});

    window.addEventListener("push-permission-state", handlePermissionState as EventListener);

    return () => {
      window.removeEventListener("push-permission-state", handlePermissionState as EventListener);
    };
  }, [isIOS, hasNativeBridge]);

  // Handle iOS FCM token
  useEffect(() => {
    if (!isIOS || !hasNativeBridge) return;

    // Listen for FCM token from iOS
    const handleToken = (event: CustomEvent) => {
      const token = event.detail;

      if (token && token !== "ERROR GET TOKEN") {
        // Update user's FCM token if user is logged in and token exists
        if (userId) {
          mutateAsync({
            userId: userId,
            fcmTokenIOS: token,
          });
        }
      }
    };

    window.addEventListener("push-token", handleToken as EventListener);

    return () => {
      window.removeEventListener("push-token", handleToken as EventListener);
    };
  }, [userId, isIOS, hasNativeBridge, mutateAsync]);

  // Handle iOS permission request results
  useEffect(() => {
    if (!isIOS || !hasNativeBridge) return;

    const handlePermissionResult = (event: CustomEvent) => {
      const result = event.detail;
      setIsNotificationPermissionGranted(result === "granted");

      // If permission granted, request FCM token
      if (result === "granted") {
        window.webkit?.messageHandlers["push-token"].postMessage({});
      }
    };

    window.addEventListener("push-permission-request", handlePermissionResult as EventListener);

    return () => {
      window.removeEventListener(
        "push-permission-request",
        handlePermissionResult as EventListener,
      );
    };
  }, [isIOS, hasNativeBridge]);

  // Web & Android notification handling
  useEffect(() => {
    // Skip if it's iOS PWA
    if (isIOS || !userId) return;

    // Check if Firebase messaging is supported in this browser
    const firebaseSupported = isFirebaseMessagingSupported();
    if (!firebaseSupported) {
      console.warn("Firebase messaging not supported in this browser");
      setIsNotificationSupported(false);
      return;
    }

    // Check if notifications are supported
    const notificationsSupported = "Notification" in window;
    setIsNotificationSupported(notificationsSupported);
    if (notificationsSupported) {
      const permissionGranted = Notification.permission === "granted";
      setIsNotificationPermissionGranted(permissionGranted);

      // If permission is already granted, get the existing FCM token
      if (permissionGranted && userId) {
        getExistingFCMToken().then((fcmToken: string | null) => {
          if (fcmToken) {
            // update fcmTokenAndroid if its android
            // update fcmTokenWeb if its not android
            if (isAndroid) {
              mutateAsync({
                userId,
                fcmTokenAndroid: fcmToken,
              });
            } else if (isAndroid === false) {
              mutateAsync({
                userId,
                fcmTokenWeb: fcmToken,
              });
            }
          }
        });
      }
    }

    // Set up the onMessage listener only if Firebase messaging is supported
    if (firebaseSupported) {
      const unsubscribe = onMessageListener().then((payload) => {
        if (payload) {
          showNotification({
            message: payload.data?.title || "",
            description: payload.data?.body || "",
            type: "success",
          });
        }
      });

      return () => {
        // @ts-ignore - Firebase type mismatch
        if (typeof unsubscribe === "function") unsubscribe();
      };
    }
  }, [isIOS, isAndroid, router, userId]);

  const requestPermission = async (): Promise<boolean> => {
    try {
      // Only if user is logged in
      if (!userId) return false;

      // iOS PWA specific implementation
      if (isIOS && hasNativeBridge) {
        // Request permission through native bridge
        window.webkit?.messageHandlers["push-permission-request"].postMessage({});

        // We return true here because the actual permission result will be handled by the event listener
        return true;
      }

      // Check if Firebase messaging is supported before proceeding
      if (!isFirebaseMessagingSupported()) {
        console.warn("Firebase messaging not supported in this browser");
        showNotification({
          message: "Notifications not supported",
          description: "Your browser doesn't support push notifications",
          type: "error",
        });
        return false;
      }

      // Web implementation (not iOS PWA)
      const fcmToken = await requestNotificationPermissionWeb();
      if (fcmToken) {
        // Update user's FCM token in the database
        if (isAndroid) {
          await mutateAsync({
            userId,
            fcmTokenAndroid: fcmToken,
          });
        } else if (isAndroid === false) {
          await mutateAsync({
            userId,
            fcmTokenWeb: fcmToken,
          });
        }

        setIsNotificationPermissionGranted(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      showNotification({
        message: "Notifications are blocked",
        description: "Please enable notifications in your browser settings",
        type: "error",
      });
      return false;
    }
  };

  return (
    <PushNotificationContext.Provider
      value={{
        isNotificationSupported,
        isNotificationPermissionGranted,
        requestPermission,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
}
